import { useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import retry from 'async-await-retry';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  versionsEndpoints as endpoints,
  brainsEndpoints,
  dialogsEndpoints,
  intentsEndpoints,
  webhooksEndpoints,
} from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { useAccount } from '@/hooks/useAccount';
import useBrains from '@/hooks/useBrains';
import { Brain } from '@/shared/types/brain';
import { Diff, Version, Versions } from '@/shared/types/version';
import { exportBrain as downloadBrain } from '@/util/fileManager';

import useHomeCheckList, { AccountUserPrefsEnum } from './useHomeCheckList';

interface PublishParams {
  description: string;
  replace_version?: number | null;
  replace_rule_ids?: string[] | null;
  force?: boolean;
}

interface PublishVersionResponse {
  version: Version;
  rule_ids: string[];
}

export const API = Object.freeze({
  listVersions: async (brainId: string): Promise<Versions> => callGet(endpoints.versions(brainId)),

  getVersion: async (brainId: string, v: number): Promise<Version> =>
    callGet(endpoints.version(brainId, v)),

  exportVersion: async (brainId: string, v: number): Promise<Version> =>
    callGet(`${endpoints.version(brainId, v)}?export=true`),

  createVersion: async (brainId: string, description: string): Promise<Version> =>
    callPost(endpoints.versions(brainId), { description }),

  deleteVersion: async (brainId: string, v: number, force?: boolean): Promise<Version> =>
    callDelete(endpoints.version(brainId, v, force)),

  revertVersion: async (brainId: string, version: number): Promise<Brain> =>
    callPut(brainsEndpoints.brain(brainId), { version }),

  getDiff: async (brainId: string, fromVersion: number, toVersion: number): Promise<Diff> =>
    callGet(`${endpoints.diff(brainId)}?from_version=${fromVersion}&to_version=${toVersion}`),

  publishVersion: async (brainId: string, params: PublishParams): Promise<PublishVersionResponse> =>
    callPost(endpoints.publish(brainId), params),
});

export const onVersionCreated = (queryClient: QueryClient, brain_id: string, version: Version) => {
  queryClient.setQueryData<Versions>([endpoints.versions(brain_id)], (prev) => ({
    versions: [version, ...(prev?.versions || [])],
  }));
  // Invalidate diff cache since the latest version changed
  queryClient.invalidateQueries({ queryKey: [endpoints.diff(brain_id)] });
  // Invalidate brain cache to update version number immediately
  queryClient.invalidateQueries({ queryKey: [brainsEndpoints.brain(brain_id)] });
};

export const onVersionRemoved = (queryClient: QueryClient, brain_id: string, version: number) => {
  queryClient.setQueryData<Versions>([endpoints.versions(brain_id)], (prev: Versions) => ({
    versions: (prev?.versions || []).filter((acc) => acc.version !== version),
  }));
  queryClient.invalidateQueries({ queryKey: [endpoints.version(brain_id, version)] });
};

interface UseVersionsProps {
  brainId: string;
  versionNumber?: number;
  enableDiff?: boolean;
  refetchInterval?: number;
}

const useVersions = ({
  brainId,
  versionNumber,
  enableDiff = false,
  refetchInterval,
}: UseVersionsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { markAsComplete } = useHomeCheckList();

  const { slug, account } = useAccount();

  const queryClient = useQueryClient();
  const { brain } = useBrains(brainId);

  const { data: versions, status: getStatus } = useQuery<Versions, Error>({
    queryKey: [endpoints.versions(brainId)],
    queryFn: () => API.listVersions(brainId),
    enabled: !!brainId,
    refetchInterval,
  });

  const { data: version } = useQuery<Version, Error>({
    queryKey: [endpoints.version(brainId, versionNumber)],
    queryFn: () => API.getVersion(brainId, versionNumber),
    enabled: !!brainId && versionNumber > 0,
  });

  // Calculate diff versions - from last published version to draft (0)
  const lastVersion = useMemo(() => {
    return versions?.versions?.find((v) => v.version !== 0) || null;
  }, [versions?.versions]);

  const fromVersion = lastVersion?.version;
  const toVersion = 0;

  const { data: differences, isLoading: differencesLoading } = useQuery<Diff, Error>({
    queryKey: [endpoints.diff(brainId), fromVersion, toVersion],
    queryFn: () => API.getDiff(brainId, fromVersion, toVersion),
    enabled: Boolean(brainId) && Boolean(fromVersion) && fromVersion !== toVersion && enableDiff,
  });

  const {
    mutate: createVersion,
    mutateAsync: createVersionAsync,
    status: createStatus,
    isPending: isCreatePending,
  } = useMutation<Version, Error, { description: string; hideToast?: boolean }>({
    mutationFn: ({ description }) => API.createVersion(brainId, description),
    onSuccess: async (resp, { hideToast = false }) => {
      // Refetch the version until the training is completed

      const isVersionTrained = async (v: Version) => {
        const result = await API.getVersion(brainId, v.version);
        if (result.status === 'available') {
          return Promise.resolve(result);
        }
        return Promise.reject(result);
      };

      const trainedVersion = await retry(isVersionTrained, [resp], {
        interval: 1500,
        exponential: true,
        factor: 2,
        retriesMax: 5,
      });

      onVersionCreated(queryClient, brainId, trainedVersion);
      markAsComplete(AccountUserPrefsEnum.CREATE_VERSION);

      queryClient.setQueryData<Version>(
        [endpoints.version(trainedVersion.brain_parent_id, trainedVersion.version)],
        () => trainedVersion
      );
      if (!hideToast) {
        toast.success(t('versions.version_created'));
      }
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteVersion, status: deleteStatus } = useMutation<
    Version,
    Error,
    { version: number; force?: boolean; hideToast?: boolean },
    boolean
  >({
    mutationFn: ({ version, force }) => API.deleteVersion(brainId, version, force),
    onSuccess: (resp, { hideToast = false }) => {
      onVersionRemoved(queryClient, brainId, resp.version);
      if (!hideToast) {
        toast.success(t('versions.version_deleted'));
      }
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: publishVersionAsync, isPending: isPublishPending } = useMutation<
    PublishVersionResponse,
    Error,
    PublishParams & { hideToast?: boolean }
  >({
    mutationFn: ({ hideToast: _, ...params }) => API.publishVersion(brainId, params),
    onSuccess: (resp, { hideToast = false }) => {
      onVersionCreated(queryClient, brainId, resp.version);
      markAsComplete(AccountUserPrefsEnum.CREATE_VERSION);
      if (resp.version.version) {
        queryClient.setQueryData<Version>(
          [endpoints.version(brainId, resp.version.version)],
          () => resp.version
        );
      }
      // Invalidate all rules queries since publish updates rules server-side
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' && query.queryKey[0].includes('/rules'),
      });
      if (!hideToast) {
        toast.success(t('versions.version_created'));
      }
    },
    meta: { errorToast: true },
  });

  const { mutate: restoreVersion, status: restoreStatus } = useMutation<Brain, Error, number>({
    mutationFn: (v) => API.revertVersion(brainId, v),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [brainsEndpoints.brain(brainId)] }),
        queryClient.invalidateQueries({ queryKey: [brainsEndpoints.brains] }),
        queryClient.invalidateQueries({ queryKey: [webhooksEndpoints.webhooks(brainId)] }),
        queryClient.invalidateQueries({ queryKey: [intentsEndpoints.intents(brainId)] }),
        queryClient.invalidateQueries({ queryKey: [dialogsEndpoints.dialogs(brainId)] }),
      ]);

      toast.success(t('brains.version_restored'));
      navigate(`/${slug}/ai-agents/${brainId}`);
    },
    meta: { errorToast: true },
  });

  const exportVersion = async (version: number) => {
    const eBrain = await callGet(`/www/api/v1/brains/${brainId}/versions/${version}?export=true`);
    downloadBrain({
      ...eBrain,
      name: `${brain?.name} - ${version == 0 ? t('common.draft') : `v${version}`}`,
    });
    toast.success(t('versions.exported', { 0: version }));
  };

  const versionsWithoutDraft = useMemo(
    () => versions?.versions.filter((x) => x.version !== 0).sort((a, b) => b.version - a.version),
    [versions]
  );

  const activeSessions = useMemo(() => {
    return versions?.versions?.reduce((acc, v) => acc + v.active_sessions, 0);
  }, [versions]);

  return {
    versions: versions?.versions,
    activeSessions,
    maxVersions: account?.max_versions,
    maxVersionsReached: versions?.versions?.length - 1 >= account?.max_versions,
    willReachVersionsLimit: versions?.versions?.length === account?.max_versions,
    getStatus,
    version,
    differences,
    differencesLoading,
    createStatus,
    deleteStatus,
    restoreStatus,
    createVersion,
    createVersionAsync,
    deleteVersion,
    restoreVersion,
    exportVersion,
    versionsWithoutDraft,
    isCreatePending,
    publishVersionAsync,
    isPublishPending,
  };
};

export default useVersions;
